import { useState, useMemo } from 'react';
import { storeProducts, appSubCategories, bookSubCategories } from '../../../mocks/storeProducts';
import { StoreProductCard } from './StoreProductCard';
import type { StoreProduct } from '../../../mocks/storeProducts';

interface Props {
  onAddToCart: (product: StoreProduct) => void;
}

type Tab = 'all' | 'book' | 'app';

export const StoreProductGrid: React.FC<Props> = ({ onAddToCart }) => {
  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [subFilter, setSubFilter] = useState('');
  const [search, setSearch] = useState('');

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setSubFilter('');
  };

  const subCategories = useMemo(() => {
    if (activeTab === 'book') return bookSubCategories;
    if (activeTab === 'app') return appSubCategories;
    return [];
  }, [activeTab]);

  const filtered = useMemo(() => {
    let list = storeProducts;
    if (activeTab !== 'all') list = list.filter(p => p.type === activeTab);
    if (subFilter && subFilter !== 'All Books' && subFilter !== 'All Apps') {
      list = list.filter(p => p.subCategory === subFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        p =>
          p.title.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.subCategory.toLowerCase().includes(q),
      );
    }
    return list;
  }, [activeTab, subFilter, search]);

  const books = storeProducts.filter(p => p.type === 'book');
  const apps = storeProducts.filter(p => p.type === 'app');

  return (
    <section className="py-16 bg-gray-50" id="books">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Search + Tabs */}
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-8">
          <div className="relative w-full md:w-80">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-sm"></i>
            </div>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search books and apps..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400"
            />
          </div>

          <div className="flex bg-white border border-gray-200 rounded-lg p-1">
            {([['all', 'All Products', `${storeProducts.length}`], ['book', 'Books', `${books.length}`], ['app', 'Apps', `${apps.length}`]] as const).map(
              ([val, label, count]) => (
                <button
                  key={val}
                  onClick={() => handleTabChange(val)}
                  className={`px-4 py-2 rounded-md text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === val
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {label} <span className="opacity-60 text-xs">({count})</span>
                </button>
              ),
            )}
          </div>
        </div>

        {/* Sub-category chips */}
        {subCategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {subCategories.map(cat => (
              <button
                key={cat}
                onClick={() => setSubFilter(subFilter === cat ? '' : cat)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  subFilter === cat
                    ? 'bg-gray-900 text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Anchor for apps section */}
        <div id="apps" />

        {/* Results count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500">
            Showing <strong className="text-gray-900">{filtered.length}</strong> product{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
              <i className="ri-search-line text-gray-400 text-2xl"></i>
            </div>
            <p className="text-gray-500 font-medium">No products found</p>
            <button
              onClick={() => { setSearch(''); setSubFilter(''); setActiveTab('all'); }}
              className="mt-3 text-gray-900 text-sm font-semibold underline cursor-pointer"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filtered.map(product => (
              <StoreProductCard key={product.id} product={product} onAddToCart={onAddToCart} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
